import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from '@common/hooks';
import { useQueryParams } from 'works/legacy/services/query-params-service';
import { DIALOG_DELAY_TO_CLOSE, DIALOG_MAIN_KEY } from '@common/services';
import { DIALOGS_MAP, DialogsMapKeyType } from './_dialogs-map';
import {
  cleanUpQueryParams,
  extractSpecificProps,
  mapPermissionsValues,
  validateDialogKeys,
} from './_dialogs-controller-service';
import { DialogsCurrentMembershipType } from './_dialogs-permissions-query';
import { useCurrentMembership } from '@identity/hooks';
import { ErrorHandler } from '@common/components';

export type DialogKeysType = DialogsMapKeyType;

export function DialogsController() {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = useQueryParams();
  const currentDialogKeys = useMemo(() => {
    const allDialogKeys = (queryParams.getAll(DIALOG_MAIN_KEY) ||
      []) as DialogsMapKeyType[];

    return unique(allDialogKeys);
  }, [queryParams]);

  const [isDOMClean, setIsDOMClean] = useState(true);
  const [currentDialogKeysWithDelay, setCurrentDialogKeysWithDelay] =
    useState<DialogsMapKeyType[]>(currentDialogKeys);

  const hasAnyDialog = !!currentDialogKeys.length;

  useEffect(() => {
    if (!hasAnyDialog) {
      setTimeout(() => {
        setIsDOMClean(true);
        setCurrentDialogKeysWithDelay(currentDialogKeys);
      }, DIALOG_DELAY_TO_CLOSE);
      return;
    }

    setIsDOMClean(false);

    if (currentDialogKeys > currentDialogKeysWithDelay) {
      setCurrentDialogKeysWithDelay(currentDialogKeys);
    }

    if (currentDialogKeys < currentDialogKeysWithDelay) {
      setTimeout(() => {
        setCurrentDialogKeysWithDelay(currentDialogKeys);
      }, DIALOG_DELAY_TO_CLOSE);
    }
  }, [hasAnyDialog, currentDialogKeys, currentDialogKeysWithDelay]);

  const currentMembership = useCurrentMembership();
  const currentUserPermissions = mapPermissionsValues(
    currentMembership || ({} as DialogsCurrentMembershipType)
  );

  if (isDOMClean || !currentMembership) {
    return null;
  }

  function updateUrlQueryParams() {
    const stringfiedQueryParams = queryParams.toString();

    navigate(`${location.pathname}?${stringfiedQueryParams}`);
  }

  function onClose(key: string) {
    cleanUpQueryParams(key, queryParams);
    updateUrlQueryParams();
  }

  const validCurrentDialogKeysWithDelay = validateDialogKeys(
    currentDialogKeysWithDelay
  );

  return (
    <>
      {validCurrentDialogKeysWithDelay.map((dialogKey) => {
        const TheDialog = DIALOGS_MAP[dialogKey];

        const canShowDialog =
          'canShow' in TheDialog ? TheDialog.canShow : () => true;

        if (!canShowDialog(currentUserPermissions)) {
          ErrorHandler.showErrorNotification('non-authorized');
          return <></>;
        }

        const props = extractSpecificProps(dialogKey, queryParams);

        return (
          <TheDialog.Component
            key={dialogKey}
            {...props}
            onClose={() => onClose(dialogKey)}
            open={currentDialogKeys.includes(dialogKey)}
          />
        );
      })}
    </>
  );
}

function unique<T>(arr: T[]) {
  return arr.filter((value, index, array) => array.indexOf(value) === index);
}
